import { Text } from '@amzn/stencil-react-components/text';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';

import { useMessages } from '../../../shared/i18n/useMessages';
import type { SubQuestionViolation } from '../../../shared/domain/subQuestionValidation';
import {
  CriteriaViolationKind,
  type CriteriaViolation,
} from '../../../shared/domain/questionConfigValidation';

const BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

/** Stable empty default so a null `violations` prop maps to an empty list. */
const NO_SUB_VIOLATIONS: readonly SubQuestionViolation[] = [];

/** Stable empty default so an omitted `criteriaViolations` prop is reference-stable. */
const NO_CRITERIA_VIOLATIONS: readonly CriteriaViolation[] = [];

/** List styling for the banner body, restored explicitly because the app's CSS
 *  reset strips default list rendering. Kept at module scope so the object is
 *  reference-stable across renders (not re-created per render in JSX). Block
 *  layout (NOT flex — flex suppresses the markers) with disc bullets and a left
 *  indent so the markers are visible; `li` spacing is a margin, not a flex gap,
 *  for the same reason. */
const BANNER_LIST_CSS = {
  margin: 0,
  paddingInlineStart: '1.25rem',
  listStyleType: 'disc',
} as const;

/** Per-item spacing between the bulleted lines (block layout, so a margin — not
 *  a flex gap). Module-scoped for reference stability. */
const BANNER_LIST_ITEM_CSS = { marginBottom: '0.25rem' } as const;

/** The runtime locale for `Intl.ListFormat`, so the conjunction ("A, B, and
 *  C") matches the user's language instead of always joining with English
 *  "and". `useMessages` exposes no locale, so read the browser's — falling
 *  back to `'en'` where `navigator` is unavailable (SSR/tests). */
function resolveLocale(): string {
  return typeof navigator !== 'undefined' && navigator.language
    ? navigator.language
    : 'en';
}

type BannerKey =
  | 'publishBlockedTitle'
  | 'publishBlockedCriteriaGroup'
  | 'publishBlockedSubGroup'
  | 'publishBlockedSubLabel'
  | 'publishBlockedCriteriaQuestionText'
  | 'publishBlockedCriteriaAuditInstruction'
  | 'publishBlockedCriteriaPassCriteria'
  | 'publishBlockedCriteriaFailCriteria'
  | 'publishBlockedCriteriaJustification';

/** The short noun-phrase i18n key for each audit-CRITERIA violation kind — the
 *  fragment that reads after "Add" inside the combined criteria line (e.g.
 *  "the question text"). A switch (not a keyed object) stays exhaustive over
 *  the union and dodges the dynamic-object-index lint. */
function criteriaFragmentKey(kind: CriteriaViolationKind): BannerKey {
  switch (kind) {
    case CriteriaViolationKind.EMPTY_QUESTION_TEXT:
      return 'publishBlockedCriteriaQuestionText';
    case CriteriaViolationKind.EMPTY_AUDIT_INSTRUCTION:
      return 'publishBlockedCriteriaAuditInstruction';
    case CriteriaViolationKind.EMPTY_PASS_CRITERIA:
      return 'publishBlockedCriteriaPassCriteria';
    case CriteriaViolationKind.EMPTY_FAIL_CRITERIA:
      return 'publishBlockedCriteriaFailCriteria';
    case CriteriaViolationKind.EMPTY_JUSTIFICATION:
      return 'publishBlockedCriteriaJustification';
  }
}

/** One bulleted line (a semantic `<li>`) in the banner body. Centralizes the
 *  shared `as`/`fontSize`/`css` so the two step groups don't repeat them. */
function BannerBullet({ children }: { children: string }): JSX.Element {
  return (
    <Text as="li" fontSize="T100" css={BANNER_LIST_ITEM_CSS}>
      {children}
    </Text>
  );
}

export interface PublishBlockedBannerProps {
  /** The live sub-question publish-block violations, or null when the draft's
   *  sub-questions are publishable. Renders nothing when both sets are
   *  empty/null. */
  readonly violations: readonly SubQuestionViolation[] | null;
  /** The live audit-CRITERIA violations (the five required rich-text fields),
   *  or null when the criteria are publishable. */
  readonly criteriaViolations?: readonly CriteriaViolation[] | null;
  readonly dataTestId?: string;
}

/**
 * The cross-step error banner shown on the final (Configuration) wizard step,
 * naming the step-1 (Audit criteria) and step-2 (Sub questions) issues still
 * blocking Save & publish. The issues are grouped into (at most) two bullets —
 * one per step:
 *
 *   • Audit criteria: Add {the failing fields, joined}.
 *   • Sub questions: Fix the error in {the offending sub-question numbers}.
 *
 * The criteria bullet names each blank field; the sub-questions bullet names
 * only WHICH sub-questions need attention (not which field) for the MLP — the
 * per-field detail lives inline on the row and can be surfaced here later if
 * users ask for it. The Configuration step's own errors are inline-only, never
 * in this banner. Non-dismissible — it's derived live from the wizard's blocks,
 * so it clears itself the instant each issue is fixed.
 */
export function PublishBlockedBanner({
  violations,
  criteriaViolations = NO_CRITERIA_VIOLATIONS,
  dataTestId,
}: PublishBlockedBannerProps): JSX.Element | null {
  const { t, f } = useMessages<BannerKey>(BUNDLE);

  const subViolations = violations ?? NO_SUB_VIOLATIONS;
  const criteriaList = criteriaViolations ?? NO_CRITERIA_VIOLATIONS;

  // Renders nothing unless criteria or sub-questions have a blocking violation.
  if (subViolations.length === 0 && criteriaList.length === 0) {
    return null;
  }

  // Join a set of fragments into one natural-language conjunction list ("A, B,
  // and C") in the user's locale.
  const joinFragments = (fragments: readonly string[]): string =>
    new Intl.ListFormat(resolveLocale(), {
      style: 'long',
      type: 'conjunction',
    }).format(fragments);

  // The Audit-criteria bullet: every blank criteria field, joined ("the
  // question text, audit instructions, and pass criteria"). `criteriaList` is
  // already in field order, so no re-sorting is needed.
  const criteriaFields = joinFragments(
    criteriaList.map((violation) => t(criteriaFragmentKey(violation.kind))),
  );

  // The Sub-questions bullet: the distinct offending sub-question numbers, in
  // row order, joined ("Sub-question 2 and Sub-question 4"). A single sub can
  // carry several violation records; collapse to its 1-based index so each
  // number appears once. A Set over the ordered indices both de-dupes and
  // preserves first-seen order.
  const subNumbers = Array.from(new Set(subViolations.map((v) => v.index)));
  const subLabels = joinFragments(
    subNumbers.map((index) => f('publishBlockedSubLabel', { number: index })),
  );

  return (
    <MessageBanner
      type={MessageBannerType.Error}
      isDismissible={false}
      titleText={t('publishBlockedTitle')}
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      {/* A real <ul> (Text as="ul") so the two step groups read as a bulleted
       *  list with native disc markers and correct a11y (screen readers
       *  announce "list, N items"). Stencil ships no dedicated List primitive,
       *  so rendering Text as ul/li keeps the typography tokens while producing
       *  semantic markup. Styling lives in module-scoped constants (block
       *  layout — flex would hide the markers). */}
      <Text as="ul" css={BANNER_LIST_CSS}>
        {/* Audit-criteria group first (step 1), then sub-questions (step 2) —
         *  reading order matches the stepper. Each group is one bullet, shown
         *  only when that step has a blocking violation. */}
        {criteriaList.length > 0 ? (
          <BannerBullet>
            {f('publishBlockedCriteriaGroup', { fields: criteriaFields })}
          </BannerBullet>
        ) : null}
        {subViolations.length > 0 ? (
          <BannerBullet>{f('publishBlockedSubGroup', { subs: subLabels })}</BannerBullet>
        ) : null}
      </Text>
    </MessageBanner>
  );
}
